import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, createColumnHelper,
  flexRender, type SortingState,
} from '@tanstack/react-table';
import StatusBadge from '../../../components/base/StatusBadge';
import {
  deletePayment,
  fetchPaymentsList,
  type AdminPayment,
} from '../../../api/payments.api';
import { formatPlanMoney } from '../../../lib/planCurrency';
import { usePlatformPermissions } from '../../../hooks/usePlatformPermissions';
import EditPaymentModal from './EditPaymentModal';

const columnHelper = createColumnHelper<AdminPayment>();
type FilterType = 'all' | 'paid' | 'pending' | 'refunded' | 'failed';

export default function PaymentsTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePlatformPermissions();
  const canEdit = can('edit_payment');
  const canDelete = can('delete_payment');

  const [rows, setRows] = useState<AdminPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterType>('all');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [editPayment, setEditPayment] = useState<AdminPayment | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const r = await fetchPaymentsList({
      page,
      per_page: 20,
      status: statusFilter === 'all' ? undefined : statusFilter,
      search: globalFilter.trim() || undefined,
    });
    setLoading(false);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(r.message);
      setRows([]);
      return;
    }
    setRows(r.data);
    setLastPage(Math.max(1, r.last_page));
    setTotal(r.total);
  }, [page, statusFilter, globalFilter, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = useCallback(async (payment: AdminPayment) => {
    const label = payment.reference ?? `#${payment.id}`;
    const confirmed = window.confirm(t('payments.confirm_delete', { ref: label }));
    if (!confirmed) return;

    setDeletingId(payment.id);
    setError('');
    setSuccessMessage('');
    const result = await deletePayment(payment.id);
    setDeletingId(null);
    if (result.ok === false) {
      if (result.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(result.message);
      return;
    }
    setSuccessMessage(t('payments.delete_success'));
    void load();
  }, [load, navigate, t]);

  const revenueByCurrency = useMemo(() => {
    return rows
      .filter((p) => p.status === 'paid')
      .reduce<Record<string, number>>((acc, p) => {
        const code = (p.currency || 'EGP').toUpperCase();
        acc[code] = (acc[code] ?? 0) + Number(p.amount || 0);
        return acc;
      }, {});
  }, [rows]);

  const revenueLabel = Object.keys(revenueByCurrency).length === 0
    ? '—'
    : Object.entries(revenueByCurrency)
        .map(([code, totalAmt]) => formatPlanMoney(totalAmt, code))
        .join(' · ');

  const totalPaid = rows.filter((p) => p.status === 'paid').length;
  const totalPending = rows.filter((p) => p.status === 'pending').length;
  const totalRefunded = rows.filter((p) => p.status === 'refunded').length;

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor((row) => row.reference ?? `#${row.id}`, {
        id: 'reference',
        header: () => t('payments.col.invoice'),
        cell: (info) => (
          <span className="text-xs font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor((row) => row.tenant_name ?? '—', {
        id: 'tenant',
        header: () => t('payments.col.atelier'),
        cell: (info) => (
          <div>
            <p className="text-sm font-semibold text-gray-900">{info.getValue()}</p>
            <p className="text-xs text-gray-400">{info.row.original.tenant_slug}</p>
          </div>
        ),
      }),
      columnHelper.accessor('amount', {
        header: () => t('payments.col.amount'),
        cell: (info) => (
          <div>
            <p className="text-sm font-bold text-gray-900">
              {formatPlanMoney(info.getValue(), info.row.original.currency)}
            </p>
            <p className="text-xs text-gray-400">{info.row.original.plan_name ?? '—'}</p>
          </div>
        ),
      }),
      columnHelper.accessor((row) => row.method ?? '—', {
        id: 'method',
        header: () => t('payments.col.method'),
        cell: (info) => <span className="text-sm text-gray-700 capitalize">{info.getValue()}</span>,
      }),
      columnHelper.accessor('status', {
        header: () => t('payments.col.status'),
        cell: (info) => <StatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor((row) => row.paid_at ?? row.created_at ?? '', {
        id: 'paid_at',
        header: () => t('payments.col.date'),
        cell: (info) => (
          <span className="text-sm text-gray-600">
            {info.getValue() ? new Date(info.getValue()).toLocaleDateString('ar-EG') : '—'}
          </span>
        ),
      }),
    ];

    if (canEdit || canDelete) {
      cols.push(
        columnHelper.display({
          id: 'actions',
          header: () => t('payments.col.actions'),
          cell: ({ row }) => {
            const payment = row.original;
            return (
              <div className="flex items-center gap-2 flex-wrap">
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSuccessMessage('');
                      setEditPayment(payment);
                    }}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-teal-50 text-teal-700 hover:bg-teal-100 cursor-pointer"
                  >
                    <i className="ri-edit-line" />
                    {t('payments.edit.action')}
                  </button>
                ) : null}
                {canDelete ? (
                  <button
                    type="button"
                    disabled={deletingId === payment.id}
                    onClick={() => void handleDelete(payment)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50 cursor-pointer"
                  >
                    <i
                      className={
                        deletingId === payment.id
                          ? 'ri-loader-4-line animate-spin'
                          : 'ri-delete-bin-line'
                      }
                    />
                    {t('actions.delete')}
                  </button>
                ) : null}
              </div>
            );
          },
        }),
      );
    }

    return cols;
  }, [t, canEdit, canDelete, deletingId, handleDelete]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const filters: { value: FilterType; labelKey: string }[] = [
    { value: 'all', labelKey: 'payments.filter.all' },
    { value: 'paid', labelKey: 'payments.filter.paid' },
    { value: 'pending', labelKey: 'payments.filter.pending' },
    { value: 'refunded', labelKey: 'payments.filter.refunded' },
    { value: 'failed', labelKey: 'payments.filter.failed' },
  ];

  const colSpan = canEdit || canDelete ? 7 : 6;

  return (
    <div className="flex flex-col gap-5">
      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      ) : null}

      {successMessage ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {successMessage}
        </div>
      ) : null}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: t('payments.summary.total_revenue'), value: loading ? '…' : revenueLabel, icon: 'ri-money-dollar-circle-line', color: 'bg-teal-50 text-teal-600' },
          { label: t('payments.summary.paid'), value: String(totalPaid), icon: 'ri-checkbox-circle-line', color: 'bg-emerald-50 text-emerald-600' },
          { label: t('payments.summary.pending'), value: String(totalPending), icon: 'ri-time-line', color: 'bg-amber-50 text-amber-600' },
          { label: t('payments.summary.refunded'), value: String(totalRefunded), icon: 'ri-refund-2-line', color: 'bg-rose-50 text-rose-600' },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-xl p-4 ring-1 ring-gray-100 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${card.color}`}>
              <i className={`${card.icon} text-lg`} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-400 font-medium">{card.label}</p>
              <p className="text-lg font-bold text-gray-900 truncate">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl ring-1 ring-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {filters.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => { setStatusFilter(f.value); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
                  statusFilter === f.value ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>
          <input
            value={globalFilter}
            onChange={(e) => { setGlobalFilter(e.target.value); setPage(1); }}
            placeholder={t('payments.search_placeholder')}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="bg-gray-50/80">
                  {hg.headers.map((h) => (
                    <th key={h.id} className="px-4 py-3 text-start text-xs font-semibold text-gray-500">
                      {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={colSpan} className="px-4 py-8 text-center text-sm text-gray-400">…</td></tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr><td colSpan={colSpan} className="px-4 py-8 text-center text-sm text-gray-400">{t('payments.no_payments')}</td></tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-t border-gray-50 hover:bg-gray-50/50">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-between text-sm text-gray-500">
          <span>{total} {t('payments.payments_count')}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
            >
              ‹
            </button>
            <span>{page} / {lastPage}</span>
            <button
              type="button"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {canEdit ? (
        <EditPaymentModal
          open={!!editPayment}
          payment={editPayment}
          onClose={() => setEditPayment(null)}
          onSaved={() => {
            setSuccessMessage(t('payments.edit.success'));
            void load();
          }}
        />
      ) : null}
    </div>
  );
}
